/**
 * Coordinates video playback across the app to work around iOS Safari's
 * hardware decoder limit (~4-6 concurrent videos).
 *
 * On desktop browsers this limit does not exist, so the coordinator
 * no-ops and all videos play independently.
 *
 * When a priority video (Lightbox, Create preview, Result) needs to play,
 * all grid/card videos should pause to free up decoder resources.
 *
 * Uses reference counting to handle multiple simultaneous priority sources.
 * Videos only resume when ALL priority sources have deactivated.
 */

import { isMobile, isTablet } from '@/consts/device';

// Only apply pause coordination on mobile/tablet where decoder limits exist
const shouldCoordinate = isMobile || isTablet;

type PauseCallback = () => void;
type ResumeCallback = () => void;

interface VideoRegistration {
  pause: PauseCallback;
  resume: ResumeCallback;
}

type RegisterCardVideoParams = {
  id: string;
  pause: PauseCallback;
  resume: ResumeCallback;
};

// All registered card/grid videos
const cardVideos = new Map<string, VideoRegistration>();

// Reference count for priority videos (Lightbox, Create, Result can all be active)
// Videos only resume when this reaches 0
let priorityVideoCount = 0;

/**
 * Register a card/grid video so it can be paused when priority videos need resources.
 */
export const registerCardVideo = ({
  id,
  pause,
  resume,
}: RegisterCardVideoParams): void => {
  cardVideos.set(id, { pause, resume });
};

/**
 * Unregister a card/grid video when it unmounts.
 * @param id - The video's unique identifier
 */
export const unregisterCardVideo = (id: string): void => {
  cardVideos.delete(id);
};

/**
 * Called when a priority video (Lightbox, Create preview, Result) activates.
 * Pauses all card videos to free up iOS decoder resources.
 * No-ops on desktop where decoder limits don't apply.
 *
 * Uses reference counting - multiple sources can activate without conflict.
 */
export const activatePriorityVideo = (): void => {
  if (!shouldCoordinate) return;

  const wasInactive = priorityVideoCount === 0;
  priorityVideoCount++;

  // Only pause videos on first activation
  if (wasInactive) {
    cardVideos.forEach((registration) => {
      try {
        registration.pause();
      } catch (e) {
        // Ignore errors from already-paused videos
      }
    });
  }
};

/**
 * Called when priority video deactivates (Lightbox closes, etc).
 * Resumes visible card videos only when ALL priority sources have deactivated.
 * No-ops on desktop where decoder limits don't apply.
 */
export const deactivatePriorityVideo = (): void => {
  if (!shouldCoordinate) return;

  // Prevent going negative (safety check)
  if (priorityVideoCount > 0) {
    priorityVideoCount--;
  }

  // Only resume videos when all priority sources are inactive
  if (priorityVideoCount === 0) {
    cardVideos.forEach((registration) => {
      try {
        registration.resume();
      } catch (e) {
        // Ignore errors from unmounted videos
      }
    });
  }
};

/**
 * Check if a priority video is currently active.
 * Card videos should not play while this returns true.
 * Always returns false on desktop.
 */
export const isPriorityVideoActive = (): boolean => {
  if (!shouldCoordinate) return false;
  return priorityVideoCount > 0;
};
